import { FormEvent, ReactNode, useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, ClipboardList, Package, RefreshCw, Search, Send, Warehouse } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminErpDashboard,
  AdminErpInventoryItem,
  AdminErpOutboundOrder,
  AdminErpPurchaseOrder,
  AdminErpStoreOrderCandidate,
  AdminStoreSalesOrderOption,
  AdminUser,
  getAdminErpDashboard,
  getCurrentAdmin,
  listAdminErpInventory,
  listAdminErpOutboundOrders,
  listAdminErpPurchaseOrders,
  logout,
  PaginationMeta,
} from "./api";

type View = "dashboard" | "inventory" | "purchase" | "outbound";

const emptyPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function count(value?: number | null) {
  return `${Number(value || 0).toLocaleString("zh-TW")} 筆`;
}

function pageTitle(view: View) {
  if (view === "inventory") return "庫存總覽";
  if (view === "purchase") return "採購單";
  if (view === "outbound") return "出庫作業";
  return "ERP 總覽";
}

function MetricCard({ icon, label, note, tone = "default", value }: { icon: ReactNode; label: string; note?: string; tone?: "default" | "warning" | "danger"; value: string }) {
  const toneClass = tone === "danger" ? "border-l-[#a33a2f]" : tone === "warning" ? "border-l-[#b7791f]" : "border-l-[#315746]";
  const iconClass = tone === "danger" ? "bg-[#fdebea] text-[#8a1f1f]" : tone === "warning" ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]";
  return (
    <div className={`rounded-md border border-l-4 border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-[#69776f]">{label}</p>
          <p className="mt-2 break-words text-[28px] font-extrabold leading-tight text-[#203d32]">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>{icon}</span>
      </div>
      {note ? <p className="mt-3 break-words text-sm font-semibold text-[#7b877f]">{note}</p> : null}
    </div>
  );
}

function StatusBadge({ children }: { children: ReactNode }) {
  return <span className="inline-flex min-h-7 items-center rounded-full bg-[#edf5f0] px-3 py-1 text-xs font-extrabold text-[#315746]">{children}</span>;
}

function EmptyState({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#fbfcfb] px-4 py-10 text-center font-semibold text-[#7b877f]">{text}</div>;
}

function Pagination({ disabled, pagination, onChange }: { disabled?: boolean; pagination: PaginationMeta; onChange: (page: number) => void }) {
  return (
    <div className="flex flex-col gap-3 border-t border-[#edf0ee] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-bold text-[#59665f]">共 {pagination.Total.toLocaleString("zh-TW")} 筆</p>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:flex">
        <button className="h-10 rounded-md border border-[#dfe6e1] bg-white px-4 font-bold text-[#315746] disabled:opacity-45" disabled={disabled || pagination.Page <= 1} onClick={() => onChange(pagination.Page - 1)} type="button">上一頁</button>
        <span className="px-3 text-center font-extrabold text-[#203d32]">{pagination.Page} / {pagination.TotalPages}</span>
        <button className="h-10 rounded-md border border-[#dfe6e1] bg-white px-4 font-bold text-[#315746] disabled:opacity-45" disabled={disabled || pagination.Page >= pagination.TotalPages} onClick={() => onChange(pagination.Page + 1)} type="button">下一頁</button>
      </div>
    </div>
  );
}

function OrderCandidateList({ title, rows }: { title: string; rows: AdminErpStoreOrderCandidate[] }) {
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-extrabold text-[#203d32]">{title}</h2>
        <span className="text-sm font-bold text-[#7b877f]">{count(rows.length)}</span>
      </div>
      {rows.length === 0 ? <EmptyState text="目前沒有需要接手的門市訂單。" /> : null}
      <div className="grid gap-3">
        {rows.map((row) => (
          <article className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" key={row.StoreSalesOrderId}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="break-all text-lg font-extrabold text-[#203d32]">{row.OrderNumber}</p>
                <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{row.LocationName || "-"} / {row.CustomerName} / {row.CustomerPhone}</p>
              </div>
              <StatusBadge>{row.StatusLabel}</StatusBadge>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <div className="rounded-md bg-white px-3 py-2">
                <p className="text-xs font-bold text-[#7b877f]">商品</p>
                <p className="mt-1 break-words font-bold text-[#203d32]">{row.ProductSummary || "-"}</p>
              </div>
              <div className="rounded-md bg-white px-3 py-2">
                <p className="text-xs font-bold text-[#7b877f]">成交金額</p>
                <p className="mt-1 font-bold text-[#203d32]">{money(row.TotalAmount)}</p>
              </div>
              <div className="rounded-md bg-white px-3 py-2">
                <p className="text-xs font-bold text-[#7b877f]">剩餘待付</p>
                <p className="mt-1 font-bold text-[#203d32]">{money(row.BalanceAmount)}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function FilterForm({ keyword, status, statusOptions, loading, onKeyword, onReset, onSearch, onStatus }: { keyword: string; status: string; statusOptions?: AdminStoreSalesOrderOption[]; loading?: boolean; onKeyword: (value: string) => void; onReset: () => void; onSearch: () => void; onStatus?: (value: string) => void }) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSearch();
  }
  return (
    <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={submit}>
      <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_220px_auto] lg:items-end">
        <label className="min-w-0">
          <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
          <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="單號、商品、SKU" value={keyword} onChange={(event) => onKeyword(event.target.value)} />
        </label>
        {statusOptions && onStatus ? (
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => onStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
        ) : null}
        <div className="rwd-action-grid lg:min-w-[260px]">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit">
            <Search size={18} />
            查詢
          </button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={onReset} type="button">
            <RefreshCw size={18} />
            重整
          </button>
        </div>
      </div>
    </form>
  );
}

function DashboardView({ data, loading }: { data: AdminErpDashboard | null; loading: boolean }) {
  const metrics = data?.Metrics;
  return (
    <div className="space-y-5">
      <section className="rounded-md border border-[#dfe6e1] bg-white p-5 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <p className="text-sm font-extrabold text-[#69776f]">ERP 第一階段</p>
        <div className="mt-2 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <h2 className="break-words text-2xl font-extrabold text-[#203d32]">接住門市訂單、庫存、採購與出庫流程</h2>
            <p className="mt-2 max-w-4xl text-sm font-semibold leading-7 text-[#59665f]">目前先與既有商品庫存、門市訂單狀態同步；後續可擴充正式入庫、序號級庫存、採購收貨、工程揀貨與出庫扣帳。</p>
          </div>
        </div>
      </section>

      {loading && !data ? <EmptyState text="ERP 資料載入中..." /> : null}
      {metrics ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={<ClipboardList size={22} />} label="待工程部備貨" value={count(metrics.PendingEngineering)} note="總部確認後交由工程處理" />
            <MetricCard icon={<Package size={22} />} label="待廠商採購" value={count(metrics.WaitingPurchase)} note="無庫存或需向廠商採購" tone={metrics.WaitingPurchase > 0 ? "warning" : "default"} />
            <MetricCard icon={<Send size={22} />} label="待出庫" value={count(metrics.PreparingOutbound)} note="準備出庫到指定門市" />
            <MetricCard icon={<Warehouse size={22} />} label="已出庫到店" value={count(metrics.ShippedToStore + metrics.ReadyForPickup)} note="可進入通知取件流程" />
            <MetricCard icon={<Package size={22} />} label="SKU 數" value={count(metrics.SkuCount)} note={`既有庫存 ${metrics.LegacyStockTotal.toLocaleString("zh-TW")} 件`} />
            <MetricCard icon={<Warehouse size={22} />} label="ERP 現貨" value={`${metrics.ErpInStockTotal.toLocaleString("zh-TW")} 件`} note={`保留 ${metrics.ErpReservedTotal.toLocaleString("zh-TW")} 件`} />
            <MetricCard icon={<AlertTriangle size={22} />} label="低庫存" value={count(metrics.LowStockCount)} note="可作為採購提醒" tone={metrics.LowStockCount > 0 ? "warning" : "default"} />
            <MetricCard icon={<AlertTriangle size={22} />} label="無庫存" value={count(metrics.OutOfStockCount)} note="需注意接單與採購" tone={metrics.OutOfStockCount > 0 ? "danger" : "default"} />
          </div>

          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <h2 className="mb-4 text-base font-extrabold text-[#203d32]">門市訂單到 ERP 的作業流</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
              {data.StoreOrderFlow.map((item, index) => (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" key={item.Key}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#edf5f0] font-extrabold text-[#315746]">{index + 1}</span>
                    {index < data.StoreOrderFlow.length - 1 ? <ArrowRight className="hidden text-[#9aa59f] xl:block" size={18} /> : null}
                  </div>
                  <p className="mt-3 min-h-[44px] text-sm font-extrabold text-[#203d32]">{item.Label}</p>
                  <p className="mt-2 text-2xl font-extrabold text-[#203d32]">{item.Count.toLocaleString("zh-TW")}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function InventoryView() {
  const [keyword, setKeyword] = useState("");
  const [stockLevel, setStockLevel] = useState("");
  const [items, setItems] = useState<AdminErpInventoryItem[]>([]);
  const [counts, setCounts] = useState({ Total: 0, Available: 0, Low: 0, Out: 0 });
  const [pagination, setPagination] = useState(emptyPagination);
  const [options, setOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextStockLevel = stockLevel) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listAdminErpInventory({ Keyword: nextKeyword.trim(), StockLevel: nextStockLevel, Page: page, PerPage: 20 });
      setItems(data.Items);
      setCounts(data.Counts);
      setPagination(data.Pagination);
      setOptions(data.StockLevelOptions);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "庫存總覽載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    setKeyword("");
    setStockLevel("");
    void load(1, "", "");
  }

  return (
    <div>
      <FilterForm keyword={keyword} loading={loading} onKeyword={setKeyword} onReset={reset} onSearch={() => load(1)} status={stockLevel} statusOptions={options} onStatus={setStockLevel} />
      {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={<Package size={22} />} label="庫存項目" value={count(counts.Total)} />
        <MetricCard icon={<Warehouse size={22} />} label="有可用庫存" value={count(counts.Available)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="低庫存" value={count(counts.Low)} tone={counts.Low > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="無庫存" value={count(counts.Out)} tone={counts.Out > 0 ? "danger" : "default"} />
      </div>
      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <h2 className="text-base font-extrabold text-[#203d32]">庫存列表</h2>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        {items.length === 0 && !loading ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的庫存項目。" /></div> : null}
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] table-fixed text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[150px] px-4 py-3">SKU</th>
                  <th className="px-4 py-3">商品 / 規格</th>
                  <th className="w-[110px] px-4 py-3 text-right">既有庫存</th>
                  <th className="w-[110px] px-4 py-3 text-right">ERP 現貨</th>
                  <th className="w-[100px] px-4 py-3 text-right">保留</th>
                  <th className="w-[100px] px-4 py-3 text-right">出庫中</th>
                  <th className="w-[100px] px-4 py-3 text-right">可用</th>
                  <th className="w-[150px] px-4 py-3">資料來源</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr key={`${item.ProductId}-${item.VariantId || "single"}`}>
                    <td className="break-all px-4 py-4 font-bold text-[#203d32]">{item.Sku}</td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p>
                      <p className="mt-1 break-words text-sm font-semibold text-[#7b877f]">{item.VariantLabel || "單一規格"}</p>
                    </td>
                    <td className="px-4 py-4 text-right font-bold">{item.LegacyStock}</td>
                    <td className="px-4 py-4 text-right font-bold">{item.ErpInStock}</td>
                    <td className="px-4 py-4 text-right">{item.ErpReserved}</td>
                    <td className="px-4 py-4 text-right">{item.ErpOutbound}</td>
                    <td className="px-4 py-4 text-right font-extrabold text-[#203d32]">{item.AvailableStock}</td>
                    <td className="px-4 py-4"><StatusBadge>{item.Source}</StatusBadge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function ErpOrderTable<T extends AdminErpPurchaseOrder | AdminErpOutboundOrder>({ rows, type }: { rows: T[]; type: "purchase" | "outbound" }) {
  if (rows.length === 0) return <EmptyState text={type === "purchase" ? "目前尚未建立採購單。" : "目前尚未建立出庫單。"} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] table-fixed text-left text-sm">
        <thead className="bg-[#f3f7f4] text-[#59665f]">
          <tr>
            <th className="w-[170px] px-4 py-3">{type === "purchase" ? "採購單號" : "出庫單號"}</th>
            <th className="w-[150px] px-4 py-3">狀態</th>
            <th className="px-4 py-3">來源門市訂單</th>
            <th className="w-[180px] px-4 py-3">{type === "purchase" ? "供應商 / 預計到貨" : "倉庫 / 到店門市"}</th>
            <th className="w-[120px] px-4 py-3 text-right">品項 / 數量</th>
            <th className="w-[160px] px-4 py-3">建立時間</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#edf0ee]">
          {rows.map((row) => {
            const purchase = row as AdminErpPurchaseOrder;
            const outbound = row as AdminErpOutboundOrder;
            const key = type === "purchase" ? purchase.ErpPurchaseOrderId : outbound.ErpOutboundOrderId;
            const number = type === "purchase" ? purchase.PurchaseNumber : outbound.OutboundNumber;
            return (
              <tr key={key}>
                <td className="break-all px-4 py-4 font-extrabold text-[#203d32]">{number}</td>
                <td className="px-4 py-4"><StatusBadge>{row.StatusLabel}</StatusBadge></td>
                <td className="break-all px-4 py-4 font-bold text-[#59665f]">{row.StoreOrderNumber || "-"}</td>
                <td className="px-4 py-4">
                  {type === "purchase" ? (
                    <>
                      <p className="break-words font-bold text-[#203d32]">{purchase.SupplierName || "-"}</p>
                      <p className="mt-1 text-[#7b877f]">{purchase.ExpectedArrivalDate || "-"}</p>
                    </>
                  ) : (
                    <>
                      <p className="break-words font-bold text-[#203d32]">{outbound.FromWarehouseName || "-"}</p>
                      <p className="mt-1 break-words text-[#7b877f]">{outbound.ToLocationName || "-"}</p>
                    </>
                  )}
                </td>
                <td className="px-4 py-4 text-right font-bold">{row.ItemCount} 項 / {row.QuantityTotal}</td>
                <td className="px-4 py-4 text-[#59665f]">{row.CreatedAt || "-"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PurchaseView() {
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [orders, setOrders] = useState<AdminErpPurchaseOrder[]>([]);
  const [candidates, setCandidates] = useState<AdminErpStoreOrderCandidate[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextStatus = status) {
    setLoading(true); setMessage("");
    try {
      const data = await listAdminErpPurchaseOrders({ Keyword: nextKeyword.trim(), Status: nextStatus, Page: page, PerPage: 20 });
      setOrders(data.Orders); setCandidates(data.Candidates); setStatusOptions(data.StatusOptions); setPagination(data.Pagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "採購單載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() { setKeyword(""); setStatus(""); void load(1, "", ""); }

  return (
    <div>
      <FilterForm keyword={keyword} loading={loading} onKeyword={setKeyword} onReset={reset} onSearch={() => load(1)} status={status} statusOptions={statusOptions} onStatus={setStatus} />
      {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-base font-extrabold text-[#203d32]">採購單列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
          </div>
          <div className="px-4 pb-4"><ErpOrderTable rows={orders} type="purchase" /></div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
        </section>
        <OrderCandidateList rows={candidates} title="待採購門市訂單" />
      </div>
    </div>
  );
}

function OutboundView() {
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [orders, setOrders] = useState<AdminErpOutboundOrder[]>([]);
  const [candidates, setCandidates] = useState<AdminErpStoreOrderCandidate[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextStatus = status) {
    setLoading(true); setMessage("");
    try {
      const data = await listAdminErpOutboundOrders({ Keyword: nextKeyword.trim(), Status: nextStatus, Page: page, PerPage: 20 });
      setOrders(data.Orders); setCandidates(data.Candidates); setStatusOptions(data.StatusOptions); setPagination(data.Pagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "出庫作業載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() { setKeyword(""); setStatus(""); void load(1, "", ""); }

  return (
    <div>
      <FilterForm keyword={keyword} loading={loading} onKeyword={setKeyword} onReset={reset} onSearch={() => load(1)} status={status} statusOptions={statusOptions} onStatus={setStatus} />
      {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-base font-extrabold text-[#203d32]">出庫單列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
          </div>
          <div className="px-4 pb-4"><ErpOrderTable rows={orders} type="outbound" /></div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
        </section>
        <OrderCandidateList rows={candidates} title="待備貨 / 待出庫門市訂單" />
      </div>
    </div>
  );
}

export default function AdminErp({ view }: { view: View }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [dashboard, setDashboard] = useState<AdminErpDashboard | null>(null);
  const [loading, setLoading] = useState(view === "dashboard");
  const [message, setMessage] = useState("");

  async function loadShell() {
    try {
      const me = await getCurrentAdmin();
      setUser(me.User);
      if (view === "dashboard") {
        setLoading(true);
        const data = await getAdminErpDashboard();
        setDashboard(data);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ERP 資料載入失敗。");
      if (String(error instanceof Error ? error.message : "").includes("登入")) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadShell();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  return (
    <AdminLayout sectionLabel="ERP 管理" title={pageTitle(view)} user={user} onLogout={handleLogout}>
      {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}
      {view === "dashboard" ? <DashboardView data={dashboard} loading={loading} /> : null}
      {view === "inventory" ? <InventoryView /> : null}
      {view === "purchase" ? <PurchaseView /> : null}
      {view === "outbound" ? <OutboundView /> : null}
    </AdminLayout>
  );
}
